import React, { useState, useCallback, useEffect } from 'react'

import { PasswordRequirements } from '@platform/auth/components/PasswordRequirements'
import { RegisterData, RegisterFormState } from '@platform/auth/types/ui.types'
import { useTranslation } from '@platform/i18n/hooks/useTranslation'

import { useAuth } from '@/shared/contexts/AuthContext'
import { useTheme } from '@/shared/hooks/useTheme'
import { toBackendMessage } from '@/shared/utils/api'
import { logger } from '@/shared/utils/logger'

/**
 * Register Form Component
 * Handles user registration with email, password and personal information
 * Following Feature-Based Modular Architecture
 */

interface RegisterFormProps {
  onSuccess?: () => void
  onSwitchToLogin?: () => void
  onVerificationRequired?: () => void
  className?: string
  showSwitchToLogin?: boolean
}

const initialFormState: RegisterFormState = {
  email: '',
  password: '',
  confirmPassword: '',
  agreeToTerms: false,
  isLoading: false,
  isSubmitting: false,
  error: null,
  errors: {},
  fieldErrors: {},
}

export const RegisterForm: React.FC<RegisterFormProps> = ({
  onSuccess,
  onSwitchToLogin,
  onVerificationRequired,
  className = '',
  showSwitchToLogin = true,
}) => {
  const { t } = useTranslation('auth')
  // `errors` namespace binding for toBackendMessage synthetic transport keys
  const { t: tErrors } = useTranslation('errors')
  const [formState, setFormState] = useState<RegisterFormState>(initialFormState)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const { register, isLoading, error, clearError } = useAuth()
  const { isDark } = useTheme()

  // Theme-aware classes
  const labelClasses = isDark ? 'text-crypto-text' : 'text-gray-700'
  const inputClasses = isDark
    ? 'bg-crypto-dark-lighter border-crypto-border text-crypto-text placeholder-crypto-muted'
    : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500'
  const mutedClasses = isDark ? 'text-crypto-muted' : 'text-gray-600'
  const buttonTextClasses = isDark ? 'text-white' : 'text-white'

  // Clear auth error when component unmounts or form changes
  useEffect(() => {
    return () => {
      clearError()
    }
  }, [clearError])

  // Update loading state from context
  useEffect(() => {
    setFormState((prev) => ({ ...prev, isLoading }))
  }, [isLoading])

  // Handle input changes
  const handleInputChange = useCallback(
    (field: keyof RegisterFormState, value: string | boolean) => {
      setFormState((prev) => ({
        ...prev,
        [field]: value,
      }))

      // Clear global error when user starts typing
      if (error) {
        clearError()
      }
    },
    [error, clearError],
  )

  // No client-side validation - backend is source of truth for all validation

  // Handle form submission
  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()

      const registrationData: RegisterData = {
        email: formState.email.trim(),
        password: formState.password,
        confirmPassword: formState.confirmPassword,
        agreeToTerms: formState.agreeToTerms,
      }

      try {
        const success = await register(registrationData)

        if (success) {
          // Reset form
          setFormState(initialFormState)
          // If registration succeeded, call success callback
          // The AuthContext handles email verification - if verification is required,
          // the login is skipped and success is still returned
          onSuccess?.()
        }
      } catch (error: unknown) {
        logger.error('Registration error:', error)
        // Check if this is an EMAIL_NOT_VERIFIED error - it means registration succeeded
        const authError = error as { code?: string; error?: { code?: string } } | null
        const isVerificationRequired =
          authError?.code === 'EMAIL_NOT_VERIFIED' ||
          authError?.error?.code === 'EMAIL_NOT_VERIFIED'
        if (isVerificationRequired) {
          // Registration was successful, but email verification is required
          setFormState(initialFormState)
          onVerificationRequired?.()
        }
        // Error handling is done in the context
      }
    },
    [formState, register, onSuccess, onVerificationRequired],
  )

  // Handle key press (Enter to submit)
  const handleKeyPress = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !formState.isLoading) {
        handleSubmit(e as React.FormEvent)
      }
    },
    [handleSubmit, formState.isLoading],
  )

  return (
    <form
      onSubmit={handleSubmit}
      className={`space-y-6 ${className}`}
      noValidate
      data-testid="register-form"
    >
      {/* Email Field */}
      <div>
        <label
          htmlFor="register-email"
          className={`block text-sm font-medium mb-2 ${labelClasses}`}
        >
          {t('fields.email_address')}
        </label>
        <input
          id="register-email"
          name="email"
          type="email"
          autoComplete="email"
          required
          value={formState.email}
          onChange={(e) => handleInputChange('email', e.target.value)}
          onKeyPress={handleKeyPress}
          className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-crypto-accent focus:border-crypto-accent transition-colors ${inputClasses}`}
          placeholder={t('placeholders.enterEmail')}
          disabled={formState.isLoading}
          data-testid="register-email-input"
        />
      </div>

      {/* Password Fields */}
      <div className="space-y-4">
        {/* Password */}
        <div>
          <label
            htmlFor="register-password"
            className={`block text-sm font-medium mb-2 ${labelClasses}`}
          >
            {t('fields.password')}
          </label>
          <div className="relative">
            <input
              id="register-password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="new-password"
              required
              value={formState.password}
              onChange={(e) => handleInputChange('password', e.target.value)}
              onKeyPress={handleKeyPress}
              className={`w-full px-4 py-3 pr-12 border rounded-lg focus:outline-none focus:ring-2 focus:ring-crypto-accent focus:border-crypto-accent transition-colors ${inputClasses}`}
              placeholder={t('placeholders.createPassword')}
              disabled={formState.isLoading}
              data-testid="register-password-input"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className={`absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded transition-colors ${
                isDark
                  ? 'text-crypto-text-secondary hover:text-crypto-text'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              disabled={formState.isLoading}
              aria-label={showPassword ? t('actions.hidePassword') : t('actions.showPassword')}
              data-testid="register-password-toggle"
            >
              {showPassword ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                  />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
              )}
            </button>
          </div>
          <PasswordRequirements password={formState.password} />
        </div>

        {/* Confirm Password */}
        <div>
          <label
            htmlFor="register-confirmPassword"
            className={`block text-sm font-medium mb-2 ${labelClasses}`}
          >
            {t('fields.confirm_password')}
          </label>
          <div className="relative">
            <input
              id="register-confirmPassword"
              name="confirmPassword"
              type={showConfirmPassword ? 'text' : 'password'}
              autoComplete="new-password"
              required
              value={formState.confirmPassword}
              onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
              onKeyPress={handleKeyPress}
              className={`w-full px-4 py-3 pr-12 border rounded-lg focus:outline-none focus:ring-2 focus:ring-crypto-accent focus:border-crypto-accent transition-colors ${inputClasses}`}
              placeholder={t('placeholders.confirmPassword')}
              disabled={formState.isLoading}
              data-testid="register-confirmPassword-input"
            />
            <button
              type="button"
              onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              className={`absolute right-3 top-1/2 -translate-y-1/2 p-1 rounded transition-colors ${
                isDark
                  ? 'text-crypto-text-secondary hover:text-crypto-text'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              disabled={formState.isLoading}
              aria-label={
                showConfirmPassword ? t('actions.hidePassword') : t('actions.showPassword')
              }
              data-testid="register-confirmPassword-toggle"
            >
              {showConfirmPassword ? (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                  />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Terms and Conditions */}
      <div>
        <label className="flex items-start space-x-3">
          <input
            type="checkbox"
            checked={formState.agreeToTerms}
            onChange={(e) => handleInputChange('agreeToTerms', e.target.checked)}
            className={`mt-1 h-4 w-4 text-crypto-accent rounded focus:ring-crypto-accent focus:ring-2 ${
              isDark ? 'border-crypto-border' : 'border-gray-300'
            }`}
            disabled={formState.isLoading}
            data-testid="register-terms-checkbox"
          />
          <div className="text-sm">
            <span className={mutedClasses}>
              {t('modal.agreeToTerms')}{' '}
              <button
                type="button"
                className="text-crypto-accent hover:text-crypto-accent-light transition-colors underline"
                disabled={formState.isLoading}
                onClick={() => {
                  // Open terms modal or page - to be implemented
                }}
              >
                {t('modal.termsOfService')}
              </button>{' '}
              {t('modal.and')}{' '}
              <button
                type="button"
                className="text-crypto-accent hover:text-crypto-accent-light transition-colors underline"
                disabled={formState.isLoading}
                onClick={() => {
                  // Open privacy policy modal or page - to be implemented
                }}
              >
                {t('modal.privacyPolicy')}
              </button>
            </span>
          </div>
        </label>
      </div>

      {/* Validation Errors — siempre exponer el mensaje exacto del backend */}
      {error &&
        (() => {
          type DetailItem = { field?: string; message?: string }
          const messages: Array<{ key: string; field?: string; text: string }> = []

          if (typeof error === 'string') {
            messages.push({ key: 'global', text: error })
          } else {
            const errObj = error as { message?: string; details?: unknown }
            const details = errObj.details

            // Caso A: details es un array de errores por campo (validación Zod)
            if (Array.isArray(details)) {
              details.forEach((d: DetailItem, i: number) => {
                if (d?.message) {
                  messages.push({
                    key: d.field ?? `detail-${i}`,
                    field: d.field,
                    text: d.message,
                  })
                }
              })
            } else if (details && typeof details === 'object') {
              // Caso B: details es un objeto con su propio .message
              const nested = details as DetailItem
              if (nested.message) {
                messages.push({ key: 'nested', field: nested.field, text: nested.message })
              }
            }

            // Fallback: si details no produjo nada útil, usar el mensaje top-level
            if (messages.length === 0 && errObj.message) {
              messages.push({ key: 'global', text: errObj.message })
            }
          }

          // Último recurso: errores de transporte (NETWORK_ERROR, TIMEOUT,
          // BAD_RESPONSE) llegan con message vacío y solo un code técnico.
          // toBackendMessage resuelve un mensaje amigable localizado — nunca
          // renderizar nada (fallo silencioso) ni un código técnico crudo.
          if (messages.length === 0) {
            messages.push({ key: 'global', text: toBackendMessage(error, tErrors) })
          }

          // Una misma frase por campo se muestra UNA vez. La contraseña se valida con
          // varias reglas `.regex()` que el servidor resume en la misma frase, así que un
          // formulario vacío devolvía «no cumple los requisitos de seguridad» dos veces.
          const shownFieldMessages = new Set<string>()
          const uniqueMessages: Array<(typeof messages)[number] & { identity: string }> = []
          for (const m of messages) {
            const identity = `${m.field ?? ''}\u0000${m.text}`
            if (shownFieldMessages.has(identity)) continue
            shownFieldMessages.add(identity)
            uniqueMessages.push({ ...m, identity })
          }

          return (
            <div className="space-y-2">
              {uniqueMessages.map((m) => (
                <div
                  // Varios mensajes comparten campo (`password`), así que el campo solo no
                  // es clave: React avisaba de claves repetidas. Campo + frase es único
                  // por construcción tras deduplicar.
                  key={m.identity}
                  className="p-3 bg-red-900/20 border border-red-500/30 rounded-lg"
                  data-testid={
                    m.field ? `register-field-error-${m.field}` : 'register-global-error'
                  }
                >
                  <p className="text-sm text-red-400 [html[data-theme=light]_&]:text-red-700">
                    {m.text}
                  </p>
                </div>
              ))}
            </div>
          )
        })()}

      {/* Submit Button */}
      <button
        type="submit"
        disabled={formState.isLoading}
        className={`w-full py-3 px-4 rounded-lg font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-crypto-accent ${
          formState.isLoading
            ? 'bg-crypto-muted text-crypto-dark cursor-not-allowed'
            : `bg-crypto-accent hover:bg-crypto-accent-light ${buttonTextClasses}`
        }`}
        data-testid="register-submit-button"
      >
        {formState.isLoading ? (
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
            {t('modal.creatingAccount')}
          </div>
        ) : (
          t('actions.create_account')
        )}
      </button>

      {/* Switch to Login */}
      {showSwitchToLogin && onSwitchToLogin && (
        <div className="text-center">
          <p className={`text-sm ${mutedClasses}`}>
            {t('modal.hasAccount')}{' '}
            <button
              type="button"
              onClick={onSwitchToLogin}
              className="text-crypto-accent hover:text-crypto-accent-light font-medium transition-colors"
              disabled={formState.isLoading}
              data-testid="register-switch-to-login"
            >
              {t('actions.sign_in')}
            </button>
          </p>
        </div>
      )}
    </form>
  )
}
